/**
 * When the SPA was built, UI strings came from `generated/*.json`. After an admin publishes
 * new translations, the bundle is stale until the next deploy / `i18n:export`.
 * If `version.translationsFingerprint` in the bundle is a server-style SHA-256 (API export),
 * compare with GET /v1/i18n/version and merge GET /v1/i18n/:locale when the server tree changed.
 *
 * Offline/bootstrap exports use a length-based fingerprint — we skip remote checks then
 * (same as before: dev can still use the legacy fetch path in main.js).
 */

import { CONFIG } from '../config';
import { mergeLocaleMessagesDeep } from './mergeLocaleMessages.js';
import { escapeVueI18nAtInTranslationsTree } from '@merlot/common/vueI18nMessageEscaping.js';
import { shouldSyncPublicCmsFromApi } from './publicCmsSource.js';

const SHA256_HEX = /^[a-f0-9]{64}$/i;

/** Reused for the page session after the first successful /i18n/version response. */
let cachedTranslationVersions = null;

/**
 * No TTL: a time-based cache could return pre-publish fingerprints right after an admin publish,
 * so `serverFp === lastSynced` and we skip reloading strings incorrectly.
 * Coalesce concurrent callers onto one in-flight /i18n/version request.
 */
let inFlightTranslationVersionsPromise = null;

/** After a successful merge, avoid refetching full /i18n/:locale until the server fingerprint changes. */
const lastSyncedServerFingerprint = {};

/** Coalesce concurrent full sync operations per locale (entry-client + router.afterEach, etc.). */
const inFlightLocaleSyncByLocale = {};

/** Coalesce concurrent GET /i18n/:locale when the bundle is older than the server. */
const inFlightLocaleTranslationsByLocale = {};

export function shouldUseRemoteTranslationCheck(bundledFingerprint) {
  return SHA256_HEX.test(bundledFingerprint || '');
}

/** Call after a successful CMS publish so the next sync does not trust stale in-flight version data or last-sync hints. */
export function bumpPublishedTranslationSync() {
  for (const k of Object.keys(lastSyncedServerFingerprint)) {
    delete lastSyncedServerFingerprint[k];
  }
  cachedTranslationVersions = null;
  inFlightTranslationVersionsPromise = null;
}

/** @internal Test helper */
export function resetPublishedTranslationSyncStateForTests() {
  bumpPublishedTranslationSync();
  for (const k of Object.keys(inFlightLocaleSyncByLocale)) {
    delete inFlightLocaleSyncByLocale[k];
  }
  for (const k of Object.keys(inFlightLocaleTranslationsByLocale)) {
    delete inFlightLocaleTranslationsByLocale[k];
  }
}

async function fetchTranslationVersions() {
  if (cachedTranslationVersions) {
    return cachedTranslationVersions;
  }
  if (inFlightTranslationVersionsPromise) {
    return inFlightTranslationVersionsPromise;
  }
  const p = (async () => {
    try {
      const res = await fetch(`${CONFIG.API_BASE_URL}/i18n/version`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.success || !data.versions || typeof data.versions !== 'object') {
        throw new Error('Invalid /i18n/version payload');
      }
      cachedTranslationVersions = data.versions;
      return cachedTranslationVersions;
    } finally {
      if (inFlightTranslationVersionsPromise === p) {
        inFlightTranslationVersionsPromise = null;
      }
    }
  })();
  inFlightTranslationVersionsPromise = p;
  return p;
}

async function fetchLocaleTranslations(locale) {
  if (inFlightLocaleTranslationsByLocale[locale]) {
    return inFlightLocaleTranslationsByLocale[locale];
  }
  const p = (async () => {
    const res = await fetch(`${CONFIG.API_BASE_URL}/i18n/${locale}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    if (!data.success || !data.translations || typeof data.translations !== 'object') {
      throw new Error('Invalid /i18n/:locale payload');
    }
    return data.translations;
  })();
  inFlightLocaleTranslationsByLocale[locale] = p;
  try {
    return await p;
  } finally {
    if (inFlightLocaleTranslationsByLocale[locale] === p) {
      delete inFlightLocaleTranslationsByLocale[locale];
    }
  }
}

async function runLocaleSync({
  locale,
  fallbackMessages,
  bundledFingerprint,
  setLocaleMessage
}) {
  if (typeof window === 'undefined') return;
  if (!shouldSyncPublicCmsFromApi()) return;
  if (!shouldUseRemoteTranslationCheck(bundledFingerprint)) return;

  const versions = await fetchTranslationVersions();
  const serverFp = versions[locale];
  if (!serverFp || typeof serverFp !== 'string') return;

  // Server matches shipped bundle: published tree was reverted to export-time state.
  // Must reset vue-i18n from the bundle baseline — otherwise in-memory merge from a prior
  // newer publish stays visible until F5 (serverFp === bundled skips fetch, so merge never clears).
  if (serverFp === bundledFingerprint) {
    const baseline = fallbackMessages[locale] || {};
    setLocaleMessage(locale, JSON.parse(JSON.stringify(baseline)));
    lastSyncedServerFingerprint[locale] = serverFp;
    return;
  }

  if (serverFp === lastSyncedServerFingerprint[locale]) {
    return;
  }

  const translations = await fetchLocaleTranslations(locale);
  const base = { ...(fallbackMessages[locale] || {}) };
  setLocaleMessage(
    locale,
    mergeLocaleMessagesDeep(base, escapeVueI18nAtInTranslationsTree(translations))
  );
  lastSyncedServerFingerprint[locale] = serverFp;
}

/**
 * @param {object} opts
 * @param {string} opts.locale
 * @param {Record<string, object>} opts.fallbackMessages
 * @param {string} opts.bundledFingerprint
 * @param {(loc: string, messages: object) => void} opts.setLocaleMessage
 */
export async function syncPublishedLocaleMessagesIfNeeded({
  locale,
  fallbackMessages,
  bundledFingerprint,
  setLocaleMessage
}) {
  if (typeof window === 'undefined') return;
  if (!shouldSyncPublicCmsFromApi()) return;
  if (!shouldUseRemoteTranslationCheck(bundledFingerprint)) return;

  if (inFlightLocaleSyncByLocale[locale]) {
    return inFlightLocaleSyncByLocale[locale];
  }

  const syncPromise = (async () => {
    try {
      await runLocaleSync({
        locale,
        fallbackMessages,
        bundledFingerprint,
        setLocaleMessage
      });
    } catch (e) {
      console.warn(`i18n remote sync failed (${locale}):`, e.message);
    }
  })();

  inFlightLocaleSyncByLocale[locale] = syncPromise;
  try {
    await syncPromise;
  } finally {
    if (inFlightLocaleSyncByLocale[locale] === syncPromise) {
      delete inFlightLocaleSyncByLocale[locale];
    }
  }
}
